import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const MONETIZATION_TYPES = [
  { id: 'template', label: 'Paid Template', icon: 'Layout', color: '#6366F1', desc: 'Sell your creation as a template' },
  { id: 'game', label: 'Paid Game', icon: 'Gamepad2', color: '#10B981', desc: 'Charge for game access' },
  { id: 'video', label: 'Paid Video Asset', icon: 'Video', color: '#8B5CF6', desc: 'License your video content' },
  { id: 'music', label: 'Music Pack', icon: 'Music', color: '#F43F5E', desc: 'Sell music tracks and packs' },
];

const PAYOUT_METHODS = [
  { id: 'paypal', label: 'PayPal', icon: 'CreditCard' },
  { id: 'stripe', label: 'Stripe', icon: 'Banknote' },
  { id: 'bank', label: 'Bank Transfer', icon: 'Building2' },
];

export default function MonetizationPanel({ game, onClose }) {
  const [selectedType, setSelectedType] = useState('template');
  const [price, setPrice] = useState('4.99');
  const [payoutMethod, setPayoutMethod] = useState('paypal');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const loopNestFee = (parseFloat(price) * 0.15)?.toFixed(2);
  const creatorEarns = (parseFloat(price) * 0.85)?.toFixed(2);

  const handleSubmit = () => {
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitted(true);
    }, 1800);
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)' }}
      onClick={(e) => e?.target === e?.currentTarget && onClose?.()}
    >
      <div
        className="w-full max-w-md rounded-2xl"
        style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-xl)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--color-border)' }}>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(245,158,11,0.15)' }}>
              <Icon name="DollarSign" size={16} color="#F59E0B" />
            </div>
            <div>
              <p className="text-sm font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Monetize Creation</p>
              <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{game?.title}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-[var(--color-muted)] transition-all">
            <Icon name="X" size={16} color="var(--color-muted-foreground)" />
          </button>
        </div>

        {submitted ? (
          <div className="p-6 flex flex-col items-center gap-4 text-center">
            <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'rgba(16,185,129,0.15)' }}>
              <Icon name="CheckCircle2" size={32} color="var(--color-success)" />
            </div>
            <div>
              <p className="text-base font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Monetization Enabled!</p>
              <p className="text-sm mt-1" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
                Your creation is now listed for ${price}. You'll earn ${creatorEarns} per sale.
              </p>
            </div>
            <button
              onClick={onClose}
              className="px-6 py-2.5 rounded-xl text-sm font-semibold transition-all hover:opacity-80"
              style={{ background: 'var(--color-success)', color: 'white', fontFamily: 'var(--font-heading)' }}
            >
              Done
            </button>
          </div>
        ) : (
          <div className="p-5 flex flex-col gap-4">
            {/* Monetization type */}
            <div>
              <p className="text-xs font-bold mb-2" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Monetization Type</p>
              <div className="grid grid-cols-2 gap-2">
                {MONETIZATION_TYPES?.map((type) => {
                  const active = selectedType === type?.id;
                  return (
                    <button
                      key={type?.id}
                      onClick={() => setSelectedType(type?.id)}
                      className="flex items-center gap-2 p-2.5 rounded-xl border text-left transition-all"
                      style={{
                        borderColor: active ? type?.color : 'var(--color-border)',
                        background: active ? `${type?.color}10` : 'var(--color-muted)',
                      }}
                    >
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: active ? type?.color : `${type?.color}20` }}>
                        <Icon name={type?.icon} size={13} color={active ? '#fff' : type?.color} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-semibold truncate" style={{ color: active ? type?.color : 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>{type?.label}</p>
                        <p className="text-xs truncate" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{type?.desc}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Price */}
            <div>
              <p className="text-xs font-bold mb-2" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Set Price (USD)</p>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold" style={{ color: 'var(--color-muted-foreground)' }}>$</span>
                <input
                  type="number"
                  min="0.99"
                  max="999"
                  step="0.01"
                  value={price}
                  onChange={(e) => setPrice(e?.target?.value)}
                  className="w-full pl-7 pr-3 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                  style={{ background: 'var(--color-card)', borderColor: 'var(--color-border)', color: 'var(--color-foreground)', fontFamily: 'var(--font-data)' }}
                />
              </div>
              {/* Revenue split */}
              <div className="flex items-center justify-between mt-2 p-2.5 rounded-xl" style={{ background: 'var(--color-muted)' }}>
                <div className="text-center">
                  <p className="text-sm font-bold" style={{ color: '#F59E0B', fontFamily: 'var(--font-data)' }}>${creatorEarns}</p>
                  <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>You earn (85%)</p>
                </div>
                <Icon name="ArrowRight" size={14} color="var(--color-muted-foreground)" />
                <div className="text-center">
                  <p className="text-sm font-bold" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-data)' }}>${loopNestFee}</p>
                  <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>LoopNest (15%)</p>
                </div>
              </div>
            </div>

            {/* Payout method */}
            <div>
              <p className="text-xs font-bold mb-2" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Payout Method</p>
              <div className="flex gap-2">
                {PAYOUT_METHODS?.map((method) => (
                  <button
                    key={method?.id}
                    onClick={() => setPayoutMethod(method?.id)}
                    className="flex-1 flex flex-col items-center gap-1 py-2 rounded-xl border text-xs transition-all"
                    style={{
                      borderColor: payoutMethod === method?.id ? '#F59E0B' : 'var(--color-border)',
                      background: payoutMethod === method?.id ? 'rgba(245,158,11,0.08)' : 'var(--color-muted)',
                      color: payoutMethod === method?.id ? '#F59E0B' : 'var(--color-muted-foreground)',
                      fontFamily: 'var(--font-caption)',
                    }}
                  >
                    <Icon name={method?.icon} size={13} color={payoutMethod === method?.id ? '#F59E0B' : 'var(--color-muted-foreground)'} />
                    {method?.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Submit */}
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition-all hover:opacity-90 disabled:opacity-70"
              style={{ background: '#F59E0B', color: 'white', fontFamily: 'var(--font-heading)' }}
            >
              {isSubmitting ? <><Icon name="Loader" size={15} color="white" /> Processing...</> : <><Icon name="DollarSign" size={15} color="white" /> Enable Monetization</>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
